import React, { useState, useEffect } from 'react';
import {
  getInvoiceTemplates,
  createInvoiceTemplate,
  updateInvoiceTemplate,
  deleteInvoiceTemplate,
  previewInvoiceTemplate,
  getContentTemplates,
  createContentTemplate,
  updateContentTemplate,
  deleteContentTemplate,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  Palette, FileText, Plus, Edit2, Trash2, Eye, Star, Loader2,
} from 'lucide-react';

const LAYOUTS = [
  { value: 'classic', label: 'Clásico', desc: 'Cabecera con título grande, tabla con header de color' },
  { value: 'modern', label: 'Moderno', desc: 'Título a la derecha + barra de color decorativa' },
  { value: 'minimal', label: 'Minimalista', desc: 'Sin colores fuertes, tipografía y mucho espacio' },
  { value: 'professional', label: 'Profesional', desc: 'Banda bicolor en cabecera' },
];

const FONTS = [
  { value: 'Helvetica', label: 'Helvetica (sans-serif)' },
  { value: 'Times-Roman', label: 'Times (serif)' },
  { value: 'Courier', label: 'Courier (monoespaciada)' },
];

const emptyDesign = {
  name: '',
  layout: 'modern',
  primary_color: '#FF6B4A',
  secondary_color: '#1F2937',
  accent_color: '#FBBF24',
  font_family: 'Helvetica',
  show_logo: true,
  show_iban: true,
  show_qr_payment: false,
  show_sku: false,
  title_label: 'FACTURA',
  footer_text: '',
  payment_terms_text: '',
  is_default: false,
};

const emptyContent = {
  name: '',
  description: '',
  items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
  notes: '',
  default_due_days: 30,
};

const TemplatesPage = () => {
  const [tab, setTab] = useState('design'); // 'design' | 'content'
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Plantillas de Factura</h1>
            <p className="text-stone-500 text-sm mt-1">Personaliza cómo se ve el PDF y guarda contenido reutilizable.</p>
          </div>
          <PageHelpButton moduleKey="templates" variant="icon" />
        </div>
      </div>

      <div className="flex gap-2 border-b border-stone-200">
        <button
          onClick={() => setTab('design')}
          className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
            tab === 'design' ? 'border-[#FF6B4A] text-[#FF6B4A]' : 'border-transparent text-stone-500 hover:text-stone-800'
          }`}
          data-testid="tab-design"
        >
          <Palette className="inline w-4 h-4 mr-1.5" />
          Diseño del PDF
        </button>
        <button
          onClick={() => setTab('content')}
          className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
            tab === 'content' ? 'border-[#FF6B4A] text-[#FF6B4A]' : 'border-transparent text-stone-500 hover:text-stone-800'
          }`}
          data-testid="tab-content"
        >
          <FileText className="inline w-4 h-4 mr-1.5" />
          Contenido reutilizable
        </button>
      </div>

      {tab === 'design' ? <DesignTemplates /> : <ContentTemplates />}
    </div>
  );
};

// ============== DESIGN TEMPLATES ==============
const DesignTemplates = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDesign);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await getInvoiceTemplates();
      setList(data);
    } catch {
      toast.error('Error al cargar plantillas de diseño');
    } finally { setLoading(false); }
  };

  const openModal = (tpl = null) => {
    setEditing(tpl);
    setForm(tpl ? {
      name: tpl.name || '',
      layout: tpl.layout || 'modern',
      primary_color: tpl.primary_color || '#FF6B4A',
      secondary_color: tpl.secondary_color || '#1F2937',
      accent_color: tpl.accent_color || '#FBBF24',
      font_family: tpl.font_family || 'Helvetica',
      show_logo: tpl.show_logo !== false,
      show_iban: tpl.show_iban !== false,
      show_qr_payment: !!tpl.show_qr_payment,
      show_sku: !!tpl.show_sku,
      title_label: tpl.title_label || 'FACTURA',
      footer_text: tpl.footer_text || '',
      payment_terms_text: tpl.payment_terms_text || '',
      is_default: !!tpl.is_default,
    } : emptyDesign);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateInvoiceTemplate(editing.id, form);
        toast.success('Plantilla actualizada');
      } else {
        await createInvoiceTemplate(form);
        toast.success('Plantilla creada');
      }
      setOpen(false);
      load();
    } catch {
      toast.error('Error al guardar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await deleteInvoiceTemplate(id);
      toast.success('Eliminada');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const preview = async (tpl) => {
    setPreviewing(true);
    try {
      const { data } = await previewInvoiceTemplate(tpl.id);
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewBlobUrl(url);
    } catch {
      toast.error('Error al generar vista previa');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="new-design-template">
          <Plus className="w-4 h-4 mr-2" />
          Nueva plantilla de diseño
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
          <Palette className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-900 mb-1">Sin plantillas de diseño</h3>
          <p className="text-stone-500 mb-4">Crea tu primera plantilla para personalizar el aspecto del PDF</p>
          <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full">
            <Plus className="w-4 h-4 mr-2" />Crear plantilla
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(tpl => (
            <div key={tpl.id} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5" data-testid={`design-tpl-${tpl.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${tpl.primary_color}, ${tpl.accent_color || tpl.secondary_color})` }}>
                    <Palette className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">{tpl.name}</h3>
                    <p className="text-xs text-stone-500 capitalize">{LAYOUTS.find(l => l.value === tpl.layout)?.label || tpl.layout}</p>
                  </div>
                </div>
                {tpl.is_default && (
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
                    <Star className="w-3 h-3 inline mr-0.5" />Por defecto
                  </span>
                )}
                {tpl.system && !tpl.is_default && (
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full">
                    Sistema
                  </span>
                )}
              </div>

              <div className="flex gap-1 mb-4">
                <div className="flex-1 h-3 rounded" style={{ background: tpl.primary_color }} title="Primario" />
                <div className="flex-1 h-3 rounded" style={{ background: tpl.secondary_color }} title="Secundario" />
                <div className="flex-1 h-3 rounded" style={{ background: tpl.accent_color }} title="Acento" />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => preview(tpl)} className="rounded-xl flex-1" data-testid={`preview-design-${tpl.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" />Vista previa
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openModal(tpl)}
                  className="rounded-xl"
                  disabled={tpl.system}
                  title={tpl.system ? 'Plantilla del sistema — duplícala para editar' : 'Editar'}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => remove(tpl.id)}
                  className="rounded-xl text-red-600 hover:bg-red-50"
                  disabled={tpl.system}
                  title={tpl.system ? 'Plantilla del sistema — no se puede eliminar' : 'Eliminar'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Editar plantilla' : 'Nueva plantilla de diseño'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" placeholder="Ej: Moderna Naranja, Clásica Azul..." data-testid="design-name" />
              </div>
              <div className="space-y-2">
                <Label>Layout</Label>
                <Select value={form.layout} onValueChange={(v) => setForm({ ...form, layout: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LAYOUTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">{LAYOUTS.find(l => l.value === form.layout)?.desc}</p>
              </div>
              <div className="space-y-2">
                <Label>Tipografía</Label>
                <Select value={form.font_family} onValueChange={(v) => setForm({ ...form, font_family: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONTS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color primario</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-12 h-10 rounded-xl border border-stone-200 cursor-pointer" />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="rounded-xl font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Color secundario</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="w-12 h-10 rounded-xl border border-stone-200 cursor-pointer" />
                  <Input value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="rounded-xl font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Color de acento</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="w-12 h-10 rounded-xl border border-stone-200 cursor-pointer" />
                  <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="rounded-xl font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Título del documento</Label>
                <Input value={form.title_label} onChange={(e) => setForm({ ...form, title_label: e.target.value })} className="rounded-xl" placeholder="FACTURA" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Texto de condiciones de pago (opcional)</Label>
              <Input value={form.payment_terms_text} onChange={(e) => setForm({ ...form, payment_terms_text: e.target.value })} className="rounded-xl" placeholder="Ej: Pago a 30 días por transferencia bancaria" />
            </div>

            <div className="space-y-2">
              <Label>Pie de página (opcional)</Label>
              <Textarea value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} className="rounded-xl resize-none" rows={2} placeholder="Texto pequeño al final del PDF (ej: aviso legal)" />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                { key: 'show_logo', label: 'Mostrar logo' },
                { key: 'show_iban', label: 'Mostrar IBAN' },
                { key: 'show_sku', label: 'Mostrar SKU/Ref. en líneas' },
                { key: 'is_default', label: 'Usar como plantilla por defecto' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl cursor-pointer">
                  <span className="text-sm text-stone-700">{opt.label}</span>
                  <Switch checked={!!form[opt.key]} onCheckedChange={(c) => setForm({ ...form, [opt.key]: c })} />
                </label>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-xl">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="save-design-template">
                {editing ? 'Guardar cambios' : 'Crear plantilla'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewBlobUrl || previewing} onOpenChange={(o) => { if (!o) { if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); } }}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Vista previa del PDF</DialogTitle>
          </DialogHeader>
          <div className="flex-1 mt-4 bg-stone-100 rounded-xl overflow-hidden">
            {previewing && <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>}
            {previewBlobUrl && <iframe src={previewBlobUrl} className="w-full h-full" title="Vista previa" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== CONTENT TEMPLATES ==============
const ContentTemplates = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyContent);
  const [open, setOpen] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await getContentTemplates();
      setList(data);
    } catch {
      toast.error('Error al cargar plantillas de contenido');
    } finally { setLoading(false); }
  };

  const openModal = (tpl = null) => {
    setEditing(tpl);
    setForm(tpl ? {
      name: tpl.name || '',
      description: tpl.description || '',
      items: tpl.items?.length ? tpl.items : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
      notes: tpl.notes || '',
      default_due_days: tpl.default_due_days || 30,
    } : emptyContent);
    setOpen(true);
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 21 }] });
  const removeItem = (idx) => {
    if (form.items.length === 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: form.items.filter(it => it.description).map(it => ({
          ...it,
          quantity: parseFloat(it.quantity) || 0,
          unit_price: parseFloat(it.unit_price) || 0,
          tax_rate: parseFloat(it.tax_rate) || 21,
        })),
        default_due_days: parseInt(form.default_due_days) || 30,
      };
      if (editing) {
        await updateContentTemplate(editing.id, payload);
        toast.success('Plantilla actualizada');
      } else {
        await createContentTemplate(payload);
        toast.success('Plantilla creada');
      }
      setOpen(false);
      load();
    } catch {
      toast.error('Error al guardar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await deleteContentTemplate(id);
      toast.success('Eliminada');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="new-content-template">
          <Plus className="w-4 h-4 mr-2" />
          Nueva plantilla de contenido
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
          <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-900 mb-1">Sin plantillas de contenido</h3>
          <p className="text-stone-500 mb-4">Guarda conceptos recurrentes para usarlos al crear facturas</p>
          <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full">
            <Plus className="w-4 h-4 mr-2" />Crear plantilla
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(tpl => {
            const total = (tpl.items || []).reduce((s, it) => s + (it.quantity * it.unit_price * (1 + it.tax_rate / 100)), 0);
            return (
              <div key={tpl.id} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5" data-testid={`content-tpl-${tpl.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#FF6B4A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-stone-900 truncate">{tpl.name}</h3>
                    <p className="text-xs text-stone-500">{tpl.items?.length || 0} conceptos · vence {tpl.default_due_days}d</p>
                  </div>
                </div>
                {tpl.description && <p className="text-sm text-stone-600 mb-3 line-clamp-2">{tpl.description}</p>}
                <div className="border-t border-stone-100 pt-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Total c/IVA</span>
                  <span className="text-lg font-mono font-semibold text-[#FF6B4A]">{new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(total || 0)}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => openModal(tpl)} className="rounded-xl flex-1">
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" />Editar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(tpl.id)} className="rounded-xl text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Editar plantilla' : 'Nueva plantilla de contenido'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" placeholder="Ej: Cuota mensual desarrollo" data-testid="content-name" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" placeholder="Para qué sirve esta plantilla" />
              </div>
              <div className="space-y-2">
                <Label>Días hasta vencimiento</Label>
                <Input type="number" min="0" value={form.default_due_days} onChange={(e) => setForm({ ...form, default_due_days: e.target.value })} className="rounded-xl font-mono" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Conceptos</Label>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Descripción" className="col-span-5 rounded-xl" />
                  <Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="Cant." className="col-span-2 rounded-xl font-mono" />
                  <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} placeholder="Precio" className="col-span-2 rounded-xl font-mono" />
                  <Input type="number" step="1" value={it.tax_rate} onChange={(e) => updateItem(idx, 'tax_rate', e.target.value)} placeholder="IVA" className="col-span-2 rounded-xl font-mono" />
                  <button type="button" onClick={() => removeItem(idx)} className="col-span-1 p-2 hover:bg-red-50 rounded-xl">
                    <Trash2 className="w-4 h-4 text-red-500 mx-auto" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-xl mt-2">
                <Plus className="w-4 h-4 mr-1.5" />Añadir concepto
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl resize-none" rows={2} />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-xl">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="save-content-template">
                {editing ? 'Guardar cambios' : 'Crear plantilla'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
